/**
 * Updates the `order` field of shapes based on their
 * current visual order after drag-and-drop.
 *
 * - Uses descending order (top item = highest order)
 * - Updates only shapes belonging to the active chart
 * - Uses cached lookup maps for O(1) performance
 */
export const updateShapesOrderOptimized = (renderNodes: unknown, newArray: unknown[], activeChartId: string) => {
  // Shallow clone to avoid mutating React state directly
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const updatedShapesData = { ...(renderNodes as Record<string, unknown>) };

  // Cache: chartId -> type -> Map<shapeId, shape>
  // Prevents repeated O(n) lookups
  const shapeIndexMap: Record<string, Record<string, Map<string, unknown>>> = {};

  const total = newArray.length;
  const now = Date.now();

  // Assign descending order based on newArray position
  for (let i = 0; i < total; i++) {
    const item = newArray[i];
    const newOrder = total - i;

    // Common shapes live under chartId_common
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const chartId = (item as Record<string, unknown>)['isCommon'] ? 'chartId_common' : activeChartId;

    // Get or build fast lookup map for this chart + shape type
    const shapeMap = getShapeMap(
      updatedShapesData,
      shapeIndexMap,
      chartId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (item as Record<string, unknown>)['shape'] as string,
    );

    // Update shape if it exists
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const shape = shapeMap.get((item as Record<string, unknown>)['key'] as string);
    if (shape) {
      (shape as Record<string, unknown>)['order'] = newOrder; // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion
      (shape as Record<string, unknown>)['updatedAt'] = now; // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion
    }
  }

  return updatedShapesData;
};

/**
 * Builds and caches a Map for fast shape lookup:
 *   shapeId -> shape object
 *
 * The map is created once per chart + shape type
 * and reused across multiple updates.
 */
export const getShapeMap = (
  renderNodes: Record<string, unknown>,
  shapeIndexMap: Record<string, Record<string, Map<string, unknown>>>,
  chartId: string,
  shape: string,
): Map<string, unknown> => {
  // Ensure chart-level cache exists
  shapeIndexMap[chartId] ??= {};

  // Lazily create map for this shape type
  if (!shapeIndexMap[chartId][shape]) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const chartData = renderNodes[chartId] as Record<string, unknown[]> | undefined;
    shapeIndexMap[chartId][shape] = new Map(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (chartData?.[shape] || []).map((s: unknown) => [(s as Record<string, unknown>)['uuid'] as string, s]),
    );
  }

  return shapeIndexMap[chartId][shape];
};

/**
 * Moves an item inside an array from one index to another
 * without mutating the original array.
 *
 * Used for drag-and-drop reordering.
 */
export const moveItem = (array: unknown[], activeIndex: number, overIndex: number) => {
  const result = [...array];
  const [activeItem] = result.splice(activeIndex, 1);
  result.splice(overIndex, 0, activeItem);
  return result;
};
